/**
 * The accumulated tool-call trail behind ChatProgressPanel.
 *
 * A research turn makes many short tool calls — one production turn made 83 in
 * four minutes, each lasting 1–540ms — with long stretches of LLM inference in
 * between that publish nothing at all. An indicator that showed only the
 * in-flight tool was therefore on screen under 1% of the time and read as a
 * frozen spinner. Accumulating completed calls is what makes the panel move.
 *
 * Consecutive calls to the same tool collapse into one row with a count, because
 * the agent works a tool hard before moving on (stock_research fires
 * advance → log_caveat → get_caveats back to back). Without collapsing, a
 * last-ten list would often be ten identical rows — which looks frozen too.
 */

export interface TrailEntry {
  /** Activity key projected by the backend (`market_data`, `research_flow`, …)
   *  — never a raw plugin identifier (spec §6). */
  tool: string;
  /** Consecutive completed calls collapsed into this row. */
  count: number;
  /** Duration of the most recent call in this row, in milliseconds. */
  durationMs?: number;
}

/** Rows kept on screen. Older entries drop off the front. */
export const TRAIL_MAX = 10;

/**
 * Return a new trail with one more completed call folded in. Pure: ChatScreen
 * calls this inside a functional setState updater, which React may invoke more
 * than once, so mutating the input would double-count.
 */
export function appendTrail(trail: TrailEntry[], tool: string, durationMs?: number): TrailEntry[] {
  const last = trail[trail.length - 1];
  if (last && last.tool === tool) {
    const merged: TrailEntry = { tool, count: last.count + 1 };
    // Carry the previous duration forward when this call reported none, so the
    // column does not blank out on an untimed call.
    const carried = durationMs ?? last.durationMs;
    if (carried !== undefined) merged.durationMs = carried;
    return [...trail.slice(0, -1), merged];
  }

  const entry: TrailEntry = { tool, count: 1 };
  if (durationMs !== undefined) entry.durationMs = durationMs;
  const next = [...trail, entry];
  return next.length > TRAIL_MAX ? next.slice(next.length - TRAIL_MAX) : next;
}
